// utils
import { makeApiCall, unwrapResponse, unwrapResponseWithCode } from "@payment-client/utils/Api";
import { isEmpty } from "@swiggy-private/js-utils";

// interfaces
import * as PaymentApi from "@payment-client/interfaces/PaymentApi";
import * as CheckoutApi from "@payment-client/interfaces/Checkout";
import { PaymentFallbacksApiResponse } from "@swiggy-private/payment-ui";
import * as PennyTxnApi from "@swiggy-private/payment-types";
import { ApiResponse } from "@payment-client/interfaces/Api";
import { SplitPayMethodValues } from "@payment-client/interfaces/SplitPay";
import { BannerParams } from "@payment-client/interfaces/Banner";

// constants
import { API_SUCCESS_STATUS_CODE } from "@payment-client/constants/StatusCodes";

const ENDPOINT = {
    GET_METHODS: "/api/v1/get-payment-methods",
    GET_BANKS_LIST: "/api/v1/get-banks-list",
    GET_FALLBACK_METHODS: "/api/v1/get-payment-fallback-methods",
    CHECK_TXN_STATUS: "/api/v1/vpa-txn-status",
    CREATE_PENNY_TRANSACTION: "/api/v1/create-penny-transaction",
    CONFIRM_PENNY_TRANSACTION: "/api/v1/confirm-penny-transaction",
    REGISTER_PAY_ON_DELIVERY: "/api/v1/order/register_pay_on_delivery",
    UPDATE_FREQUENCY_COUNTER: "/api/v1/update-frequency-counter",
};

export async function getPaymentMethods(
    apiHost: string,
    queryParams: {
        isUserCredEligible: boolean;
        splitPayMethods?: string;
        isUserEligibleForSingleClick: boolean;
        isGiftingFlow: boolean;
        isInAppUpiEligible: boolean;
        turboVariant?: string;
        isBillToCompanySelected?: boolean;
        billToCompanyInfoMeta?: {
            attendeeEmail: string[];
        };
    },
): Promise<PaymentApi.GetPaymentMethodsResponse> {
    const searchParams: Record<string, string> = {
        isUserCredEligible: String(queryParams.isUserCredEligible),
        isUserEligibleForSingleClick: String(queryParams.isUserEligibleForSingleClick),
        splitPayMethods: queryParams.splitPayMethods ? String(queryParams.splitPayMethods) : SplitPayMethodValues.NONE,
        isGiftingFlow: String(queryParams.isGiftingFlow),
        isInAppUpiEligible: String(queryParams.isInAppUpiEligible),
        isBillToCompanySelected: String(queryParams.isBillToCompanySelected),
    };

    if (!isEmpty(queryParams.turboVariant)) {
        searchParams["turboVariant"] = queryParams.turboVariant;
    }

    const response = unwrapResponseWithCode(
        await makeApiCall<PaymentApi.GetPaymentMethodsResponse>({
            apiHost,
            endpoint: ENDPOINT.GET_METHODS,
            searchParams,
            options: { method: "GET" },
        }),
    );

    return response.toPromise();
}

export async function getBanksList(apiHost: string): Promise<PaymentApi.BankListData> {
    const response = unwrapResponse<PaymentApi.BankListData>(
        await makeApiCall<PaymentApi.BankListData>({ apiHost, endpoint: ENDPOINT.GET_BANKS_LIST }),
    );
    return response.toPromise();
}

export async function getPaymentFallbackOptions(
    apiHost: string,
    orderId: string,
): Promise<PaymentFallbacksApiResponse> {
    const response = unwrapResponseWithCode(
        await makeApiCall<PaymentFallbacksApiResponse>({
            apiHost,
            endpoint: ENDPOINT.GET_FALLBACK_METHODS,
            searchParams: {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                order_id: orderId,
            },
        }),
    );
    return response.toPromise();
}

export async function checkTxnStatus(apiHost: string, transactionId: string): Promise<string> {
    const response = unwrapResponse(
        await makeApiCall<string>({
            apiHost,
            endpoint: ENDPOINT.CHECK_TXN_STATUS,
            searchParams: { transactionId },
            options: {},
        }),
    );

    return response.toPromise();
}

export async function createPennyTransaction(
    apiHost: string,
    params: { [key: string]: unknown },
): Promise<PennyTxnApi.CreatePennyTransactionResponse> {
    if (__DEV__) {
        const mockCreateTransaction = document.cookie.indexOf("mock_create_order=1") !== -1;
        if (mockCreateTransaction) {
            return new Promise((resolve) => {
                console.log(params);
                const mockResponse: PennyTxnApi.CreatePennyTransactionResponse = {
                    paasId: "PD134202546693",
                    tenant: "SW-FOOD",
                    paymentMethod: "Juspay",
                    paymentPartnerOrderId: "PD134202546693",
                    paymentTxnId: "PD134202546693",
                    paymentTxnStatus: "pending",
                    binNumber: "607093",
                    subTransactionsCount: 1,
                    paymentTransactionList: [
                        {
                            paymentMethod: "Juspay",
                            paymentMethodDisplayName: "Credit/Debit Card",
                            transactionId: "PD134202546693",
                            amount: "2.0",
                            paymentMeta: {
                                binNumber: "607093",
                                paymentGateway: "",
                                externalPGResponse: undefined,
                            },
                            transactionStatus: "pending",
                            swiggyTransactionId: "com.swiggy-PD134202546693-1",
                            pgTransactionId: "",
                            couponApplied: "",
                            paymentGateway: "RAZORPAY",
                            pgResponseTime: null,
                        },
                    ],
                    amount: 2,
                    paymentGateway: "RAZORPAY",
                    juspayMeta: {
                        juspayClientAuthToken: "tkn_640c8070eb1441e99f1e010f26cd750d",
                        authTypeForCard: "THREE_DS",
                    },
                };
                setTimeout(() => resolve(mockResponse), 2000);
            });
        }
    }
    const response = unwrapResponseWithCode(
        await makeApiCall<PennyTxnApi.CreatePennyTransactionResponse>({
            apiHost,
            endpoint: ENDPOINT.CREATE_PENNY_TRANSACTION,
            bodyParams: params,
            options: {
                method: "POST",
            },
        }),
    );

    return response.toPromise();
}

export async function confirmPennyTransaction(
    apiHost: string,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    params: { order_id: string },
): Promise<ApiResponse<PennyTxnApi.ConfirmPennyTransactionResponse>> {
    if (__DEV__) {
        const mockConfirmTransaction = document.cookie.indexOf("mock_confirm_penny_transaction=1") !== -1;
        if (mockConfirmTransaction) {
            return new Promise((resolve) => {
                const mockResponse: ApiResponse<PennyTxnApi.ConfirmPennyTransactionResponse> = {
                    statusCode: 0,
                    data: {
                        tenant: "SW-FOOD",
                        paymentMethod: "Composite",
                        paymentPartnerOrderId: "PD134202236599",
                        paymentTxnId: "PD134202236599",
                        paymentTxnStatus: "success",
                        binNumber: "607093",
                        subTransactionsCount: 1,
                        paymentTransactionList: [
                            {
                                paymentMethod: "Juspay",
                                paymentMethodDisplayName: "Credit/Debit Card",
                                transactionId: "PD134202236599",
                                amount: "2.0",
                                paymentMeta: {
                                    binNumber: "607093",
                                    paymentGateway: "",
                                    externalPGResponse: undefined,
                                },
                                transactionStatus: "success",
                                swiggyTransactionId: "com.swiggy-PD134202236599-1",
                                pgTransactionId: "com.swiggy-PD134202236599-1",
                                couponApplied: "true",
                                paymentGateway: "RAZORPAY",
                            },
                        ],
                        amount: 2,
                        paymentGateway: "RAZORPAY",
                    },
                };
                setTimeout(() => resolve(mockResponse), 2500);
            });
        }
    }

    return await makeApiCall<PennyTxnApi.ConfirmPennyTransactionResponse>({
        apiHost,
        endpoint: ENDPOINT.CONFIRM_PENNY_TRANSACTION,
        bodyParams: params,
        options: {
            method: "POST",
        },
    });
}

export async function generateCartKeyForPayOnDelivery(
    apiHost: string,
    params: { orderId: string },
): Promise<ApiResponse<CheckoutApi.RequestCartKeyForPayOnDeliveryPayload>> {
    const response = await makeApiCall<CheckoutApi.RequestCartKeyForPayOnDeliveryPayload>({
        apiHost,
        endpoint: ENDPOINT.REGISTER_PAY_ON_DELIVERY,
        bodyParams: params,
        options: {
            method: "POST",
        },
    });

    if (response.statusCode !== API_SUCCESS_STATUS_CODE) {
        throw response;
    } else {
        return response;
    }
}

export async function updateFrequencyCounter(apiHost: string, params: BannerParams): Promise<void> {
    await makeApiCall({
        apiHost,
        endpoint: ENDPOINT.UPDATE_FREQUENCY_COUNTER,
        bodyParams: params as unknown as { [key: string]: unknown },
        options: { method: "POST" },
    });
}
